/** Persistência e casamento de cores — aba Lotes ↔ Produto por Palete. */

import { normalizarTextoProdutoImportacao } from './resolverProdutoPontuacao';

function normalizarChaveProdutoPalete(texto: string): string {
  return normalizarTextoProdutoImportacao(String(texto || '')).replace(/\bBALCO\b/g, 'BALCAO');
}

export function normalizarCorPalete(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Token isolado → nome usado na palete (composto usa formas curtas: OFF, NATURALE). */
function tokenParaPalete(token: string, emComposto: boolean): string {
  const t = normalizarCorPalete(token);
  if (!t) return '';

  if (t === 'OFF' || t === 'OFF WHITE' || t === 'OFFWHITE') {
    return emComposto ? 'OFF' : 'OFF WHITE';
  }
  if (t === 'NATURALE' || t === 'NATURALE NEW') {
    return emComposto ? 'NATURALE' : 'NATURALE NEW';
  }
  if (t === 'FREIJO NOBRE' || t === 'FREIJO') return 'FREIJO';
  if (t === 'FENDI') return 'FENDI';
  if (t === 'CEDRO NATURAL') return 'CEDRO';
  if (t === 'RIPADO 3D' || t === 'RIPADO') return t.includes('3D') ? 'RIPADO 3D' : 'RIPADO';

  return t;
}

/** Converte cor vinda de Lotes para o padrão de escrita da palete. */
export function montarCorPaleteDeLote(corLote: string): string {
  const raw = String(corLote || '').trim();
  if (!raw) return '';

  const partes = raw
    .split('/')
    .map((p) => p.trim())
    .filter(Boolean);

  if (partes.length === 0) return normalizarCorPalete(raw);
  if (partes.length === 1) return tokenParaPalete(partes[0], false);

  return partes.map((p) => tokenParaPalete(p, true)).join('/');
}

export function chaveCanonicaCorPalete(cor: string): string {
  return montarCorPaleteDeLote(cor);
}

/** Encontra a cor equivalente já cadastrada na palete (mantém grafia original da lista). */
export function encontrarCorPalete(corLote: string, coresPalete: string[]): string | null {
  const alvo = chaveCanonicaCorPalete(corLote);
  if (!alvo) return null;

  for (const c of coresPalete) {
    if (chaveCanonicaCorPalete(c) === alvo) return c;
  }
  return null;
}

/** Resolve cor da palete: usa existente ou cria nome canônico a partir de Lotes. */
export function resolverCorPaleteParaLote(corLote: string, coresPalete: string[]): string | null {
  const trimmed = String(corLote || '').trim();
  if (!trimmed) return null;
  return encontrarCorPalete(trimmed, coresPalete) || montarCorPaleteDeLote(trimmed);
}

/** Todas as cores distintas (nome palete) presentes nos lotes cadastrados. */
export function coletarCoresPaleteDeLotes(
  lotes: ReadonlyArray<{ corProduto?: string }>,
  coresPalete: string[]
): string[] {
  const existentes = new Set(coresPalete.map((c) => chaveCanonicaCorPalete(c)));
  const novas: string[] = [];

  for (const lp of lotes) {
    const raw = String(lp.corProduto || '').trim();
    if (!raw) continue;
    const canon = chaveCanonicaCorPalete(resolverCorPaleteParaLote(raw, [...coresPalete, ...novas]) || raw);
    if (!canon || existentes.has(canon)) continue;
    existentes.add(canon);
    novas.push(encontrarCorPalete(raw, [...coresPalete, ...novas]) || montarCorPaleteDeLote(raw));
  }

  return novas;
}

export interface QtdProdutoCorPalete {
  produtoNorm: string;
  corPalete: string;
  qtd: number;
}

/** Agrupa qtd por produto + cor (já no padrão palete) para um lote. */
export function agruparQtdPorProdutoCorDoLote(
  lotes: ReadonlyArray<{ lote: string; produtoNome: string; corProduto?: string; qtd: number }>,
  loteNorm: string,
  coresPalete: string[],
  resolverProduto: (nomeLote: string) => { chave: string } | null
): QtdProdutoCorPalete[] {
  const mapa = new Map<string, QtdProdutoCorPalete>();

  for (const lp of lotes) {
    const loteItem = normalizarCorPalete(lp.lote);
    if (loteItem !== loteNorm) continue;

    const corRaw = String(lp.corProduto || '').trim();
    if (!corRaw) continue;

    const nome = String(lp.produtoNome || '').trim();
    if (!nome) continue;
    const matchProd = resolverProduto(nome);
    if (!matchProd) continue;
    const produtoNorm = matchProd.chave;

    const corPalete =
      resolverCorPaleteParaLote(corRaw, coresPalete) || montarCorPaleteDeLote(corRaw);
    if (!corPalete) continue;

    const chave = `${produtoNorm}::${chaveCanonicaCorPalete(corPalete)}`;
    const prev = mapa.get(chave);
    const qtd = Number(lp.qtd) || 0;
    if (prev) {
      prev.qtd += qtd;
    } else {
      mapa.set(chave, { produtoNorm, corPalete, qtd });
    }
  }

  return [...mapa.values()];
}

/** Números de volume ativos (1, 2 e/ou 3) conforme cadastro do produto na palete. */
export function obterVolumesAtivosProdutoPalete(
  produtos: ReadonlyArray<{ produto: string; vol1: number; vol2?: number; vol3?: number }>,
  produtoChave: string
): number[] {
  const alvo = normalizarChaveProdutoPalete(produtoChave);
  const cfg = produtos.find((p) => normalizarChaveProdutoPalete(p.produto) === alvo);
  if (!cfg) return [1];
  const vols = [1];
  if (cfg.vol2 != null) vols.push(2);
  if (cfg.vol3 != null) vols.push(3);
  return vols;
}

/**
 * Repete a qtd total em cada volume do produto (V1, V2, V3).
 * Cada volume recebe etiquetas para todas as peças produzidas.
 */
export function distribuirQtdPorVolumesProduto(
  qtdTotal: number,
  volumes: number[]
): Array<{ vol: number; qtd: number }> {
  if (qtdTotal <= 0 || volumes.length === 0) return [];
  return volumes.map((vol) => ({ vol, qtd: qtdTotal }));
}
